import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type CSSProperties } from 'react'
import type { TenantSession } from '../lib/session'
import {
  createResidentContact,
  deleteResidentContact,
  getResidentConfig,
  importResidentCsv,
  listResidentImportDocuments,
  listResidentRecords,
  saveResidentConfig,
  storeResidentPdf,
  updateResidentContact,
  type ResidentAllowanceMode,
  type ResidentContact,
  type ResidentContactInput,
  type ResidentImportDocument,
  type ResidentRecord,
} from '../lib/api'

const card: CSSProperties = { background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 14, padding: 20, marginBottom: 16 }
const button: CSSProperties = { border: '1px solid var(--color-gray-300)', borderRadius: 10, padding: '9px 14px', background: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }
const primaryButton: CSSProperties = { ...button, color: '#fff', background: 'var(--parkify-accent, var(--color-brand))', borderColor: 'var(--parkify-accent, var(--color-brand))' }
const input: CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1.5px solid var(--color-gray-300)', borderRadius: 10, background: '#fff', fontFamily: 'inherit', fontSize: 14 }
const label: CSSProperties = { display: 'block', marginBottom: 5, fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-gray-600)' }
const RESIDENT_CAP = 3

function ResidentModal({ unit, resident, busy, onClose, onSave }: {
  unit: string
  resident?: ResidentContact
  busy: boolean
  onClose: () => void
  onSave: (input: ResidentContactInput) => Promise<string | null>
}) {
  const [firstName, setFirstName] = useState(resident?.firstName ?? '')
  const [lastName, setLastName] = useState(resident?.lastName ?? '')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState(resident?.email ?? '')
  const [error, setError] = useState('')

  const submit = async () => {
    const digits = phone.replace(/\D/g, '')
    if (!firstName.trim() || !lastName.trim()) return setError('First and last name are required.')
    if ((!resident || phone.trim()) && (digits.length < 10 || digits.length > 15)) return setError('Enter a valid mobile phone number.')
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError('Enter a valid email address or leave it blank.')
    setError('')
    const saveError = await onSave({
      firstName: firstName.trim(), lastName: lastName.trim(),
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      ...(email.trim() ? { email: email.trim() } : {}),
    })
    if (saveError) setError(saveError)
  }

  return (
    <div role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose() }} style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18, background: 'rgba(25, 19, 30, 0.42)' }}>
      <div role="dialog" aria-modal="true" aria-labelledby="resident-modal-title" style={{ width: 'min(460px, 100%)', maxHeight: 'calc(100vh - 36px)', overflowY: 'auto', borderRadius: 16, background: '#fff', boxShadow: '0 24px 70px rgba(0,0,0,.22)' }}>
        <div style={{ padding: '18px 20px 15px', borderBottom: '1px solid var(--color-gray-200)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h2 id="resident-modal-title" style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>{resident ? 'Edit resident' : 'Add resident'}</h2>
            <div style={{ marginTop: 4, fontSize: 12.5, color: 'var(--color-gray-600)' }}>{unit} · shares this apartment’s PIN and weekly hours</div>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} style={{ border: 0, background: 'transparent', color: 'var(--color-gray-500)', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            <label><span style={label}>First name</span><input autoFocus value={firstName} onChange={event => setFirstName(event.target.value)} style={input} /></label>
            <label><span style={label}>Last name</span><input value={lastName} onChange={event => setLastName(event.target.value)} style={input} /></label>
          </div>
          <label style={{ display: 'block', marginTop: 14 }}>
            <span style={label}>Phone</span>
            <input value={phone} onChange={event => setPhone(event.target.value)} inputMode="tel" autoComplete="tel" placeholder={resident ? `Leave blank to keep ••••${resident.phoneLast4}` : '(704) 555-0100'} style={input} />
          </label>
          {resident && <div style={{ marginTop: 5, fontSize: 11.5, color: 'var(--color-gray-600)' }}>For privacy, Parkify stores a one-way phone hash. Enter a number only when it needs to change.</div>}
          <label style={{ display: 'block', marginTop: 14 }}><span style={label}>Email · optional</span><input value={email} onChange={event => setEmail(event.target.value)} autoComplete="email" placeholder="resident@example.com" style={input} /></label>
          {error && <div role="alert" style={{ marginTop: 12, color: '#b91c1c', fontSize: 12.5, fontWeight: 700 }}>{error}</div>}
        </div>
        <div style={{ padding: '14px 20px 18px', borderTop: '1px solid var(--color-gray-200)', display: 'flex', justifyContent: 'flex-end', gap: 9 }}>
          <button type="button" onClick={onClose} disabled={busy} style={button}>Cancel</button>
          <button type="button" onClick={() => void submit()} disabled={busy} style={{ ...primaryButton, opacity: busy ? 0.65 : 1 }}>{busy ? 'Saving…' : resident ? 'Save changes' : 'Add resident'}</button>
        </div>
      </div>
    </div>
  )
}

export function ResidentImports({ session, showHeading = true }: { session: TenantSession; showHeading?: boolean }) {
  const [records, setRecords] = useState<ResidentRecord[]>([])
  const [documents, setDocuments] = useState<ResidentImportDocument[]>([])
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [allowanceMode, setAllowanceMode] = useState<ResidentAllowanceMode>('rolling_7_days')
  const [available, setAvailable] = useState<boolean | null>(null)
  const [search, setSearch] = useState('')
  const [modal, setModal] = useState<{ record: ResidentRecord; resident?: ResidentContact } | null>(null)

  const refresh = useCallback(async () => {
    try {
      const [recordResponse, documentResponse, config] = await Promise.all([
        listResidentRecords(session.token), listResidentImportDocuments(session.token), getResidentConfig(session.token),
      ])
      setRecords(recordResponse.records ?? [])
      setDocuments(documentResponse.documents ?? [])
      setAllowanceMode(config.allowanceMode)
      setAvailable(config.enabled)
    } catch { setMessage('Could not load resident records.') }
  }, [session.token])

  useEffect(() => {
    void getResidentConfig(session.token)
      .then(config => {
        setAllowanceMode(config.allowanceMode)
        setAvailable(config.enabled)
        if (config.enabled) void refresh()
      })
      .catch(() => setAvailable(false))
  }, [session.token, refresh])

  const base64 = async (file: File) => {
    const bytes = new Uint8Array(await file.arrayBuffer())
    let binary = ''
    for (const byte of bytes) binary += String.fromCharCode(byte)
    return btoa(binary)
  }
  const downloadTemplate = () => {
    const blob = new Blob(['Unit,Phone,Plate,Weekly guest hours,Guest PIN\nA-101,(704) 555-0100,ABC123,72,\n'], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'parkify-resident-records-template.csv'
    link.click()
    URL.revokeObjectURL(url)
  }
  const onCsv = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setBusy(true); setMessage('')
    try { await importResidentCsv(session.token, await file.text()); setMessage('Resident records imported.'); await refresh() }
    catch (error) { setMessage(error instanceof Error ? error.message : 'CSV import failed.') }
    finally { setBusy(false); event.target.value = '' }
  }
  const onPdf = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setBusy(true); setMessage('')
    try {
      await storeResidentPdf(session.token, { filename: file.name, contentType: file.type, sizeBytes: file.size, dataBase64: await base64(file) })
      setMessage('PDF saved for processing. Records will appear after extraction is enabled.')
      await refresh()
    } catch (error) { setMessage(error instanceof Error ? error.message : 'PDF upload failed.') }
    finally { setBusy(false); event.target.value = '' }
  }
  const setMode = (value: ResidentAllowanceMode) => {
    setAllowanceMode(value)
    void saveResidentConfig(session.token, value).catch(() => setMessage('Could not save allowance window.'))
  }
  const replaceRecord = (updated: ResidentRecord) => setRecords(current => current.map(record => record.unit === updated.unit ? updated : record))
  const saveContact = async (resident: ResidentContactInput): Promise<string | null> => {
    if (!modal) return 'Choose an apartment before saving.'
    setBusy(true); setMessage('')
    try {
      const response = modal.resident
        ? await updateResidentContact(session.token, modal.record.unit, modal.resident.id, resident)
        : await createResidentContact(session.token, modal.record.unit, resident)
      replaceRecord(response.record)
      setModal(null)
      setMessage(modal.resident ? 'Resident updated.' : 'Resident added.')
      return null
    } catch (error) {
      const nextMessage = error instanceof Error ? error.message : 'Could not save the resident.'
      setMessage(nextMessage)
      return nextMessage
    }
    finally { setBusy(false) }
  }
  const removeContact = async (record: ResidentRecord, resident: ResidentContact) => {
    const name = [resident.firstName, resident.lastName].filter(Boolean).join(' ') || `resident ending ${resident.phoneLast4}`
    if (!window.confirm(`Remove ${name} from ${record.unit}? They will no longer be able to open this apartment’s resident parking.`)) return
    setBusy(true); setMessage('')
    try {
      const response = await deleteResidentContact(session.token, record.unit, resident.id)
      replaceRecord(response.record)
      setMessage('Resident removed. The apartment’s PIN and allowance were kept.')
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Could not remove the resident.') }
    finally { setBusy(false) }
  }

  const visibleRecords = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return records
    return records.filter(record => [
      record.unit, ...record.plates, record.guestPin,
      ...record.residents.flatMap(resident => [resident.firstName, resident.lastName, resident.phoneLast4, resident.email ?? '']),
    ].some(value => value.toLowerCase().includes(query)))
  }, [records, search])

  if (available !== true) return null
  const shownRecords = visibleRecords.slice(0, 100)
  return <section style={{ maxWidth: 980 }}>
    {showHeading && <div style={{ marginBottom: 18 }}><div style={{ fontSize: 23, fontWeight: 800 }}>Resident guest parking</div><div style={{ color: 'var(--color-gray-600)', marginTop: 4 }}>Manage apartments and the residents who share each apartment’s guest PIN and weekly allowance.</div></div>}

    <div style={card}><div style={{ fontSize: 16, fontWeight: 800, marginBottom: 6 }}>Allowance window</div><div style={{ color: 'var(--color-gray-600)', fontSize: 13, marginBottom: 10 }}>Unused time expires. Choose when each apartment’s allowance resets.</div><select aria-label="Allowance window" value={allowanceMode} onChange={event => setMode(event.target.value as ResidentAllowanceMode)} style={{ ...button, padding: '10px 12px' }}><option value="rolling_7_days">Rolling seven days</option><option value="calendar_week">Calendar week</option></select></div>

    <div style={card}><div style={{ fontSize: 16, fontWeight: 800, marginBottom: 6 }}>Import apartments</div><div style={{ color: 'var(--color-gray-600)', fontSize: 13, lineHeight: 1.5, marginBottom: 14 }}>CSV columns: <b>Unit</b>, <b>Phone</b>, <b>Plate</b>, and optional <b>Weekly guest hours</b>. The latest import replaces the current apartment list; use the roster below for individual resident changes.</div><button type="button" onClick={downloadTemplate} style={{ ...button, marginRight: 8 }}>Download CSV template</button><label style={{ ...button, display: 'inline-block' }}>{busy ? 'Processing…' : 'Choose CSV'}<input type="file" accept=".csv,text/csv" onChange={onCsv} disabled={busy} style={{ display: 'none' }} /></label><label style={{ ...button, display: 'inline-block', marginLeft: 8, color: 'var(--color-gray-600)' }}>Choose PDF<input type="file" accept="application/pdf,.pdf" onChange={onPdf} disabled={busy} style={{ display: 'none' }} /></label><span style={{ marginLeft: 10, fontSize: 12, color: 'var(--color-gray-600)' }}>PDF extraction queued</span>{message && <div role="status" style={{ marginTop: 12, fontSize: 13, color: /Could|failed|valid|already|up to/i.test(message) ? '#b91c1c' : '#15803d', fontWeight: 700 }}>{message}</div>}</div>

    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 13 }}>
        <div style={{ flex: 1, minWidth: 180 }}><div style={{ fontSize: 16, fontWeight: 800 }}>Apartments · {records.length}</div><div style={{ color: 'var(--color-gray-600)', fontSize: 12.5, marginTop: 3 }}>Up to {RESIDENT_CAP} residents share each apartment’s PIN and hours.</div></div>
        <input aria-label="Search apartments" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search apartment or resident…" style={{ ...input, width: 'min(310px, 100%)' }} />
      </div>
      {shownRecords.length === 0 ? <div style={{ color: 'var(--color-gray-600)', fontSize: 13 }}>{records.length ? 'No apartments match that search.' : 'No resident records imported yet.'}</div> : <div style={{ display: 'grid', gap: 10 }}>
        {shownRecords.map(record => <div key={record.unit} style={{ border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: '14px 15px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 14, fontWeight: 800 }}>{record.unit}</div>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--color-gray-600)', letterSpacing: '0.04em' }}>PIN {record.guestPin}</div>
            <div style={{ flex: 1 }} />
            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--color-gray-600)' }}>{record.weeklyGuestHours} hours/week · {record.plates.length} plate{record.plates.length === 1 ? '' : 's'}</div>
          </div>
          {record.residents.length === 0 ? <div style={{ marginTop: 8, fontSize: 12, color: 'var(--color-gray-600)' }}>Nobody is registered to this apartment yet.</div> : record.residents.map(resident => {
            const residentName = [resident.firstName, resident.lastName].filter(Boolean).join(' ') || `Resident ending ${resident.phoneLast4}`
            return <div key={resident.id} style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginTop: 9, paddingTop: 9, borderTop: '1px solid var(--color-gray-100)' }}>
              <div style={{ flex: '1 1 210px', minWidth: 0 }}><div style={{ fontSize: 13, fontWeight: 750 }}>{residentName}</div><div style={{ marginTop: 2, fontSize: 11.5, color: 'var(--color-gray-600)' }}>••••{resident.phoneLast4}{resident.email ? ` · ${resident.email}` : ''}</div></div>
              <button type="button" onClick={() => setModal({ record, resident })} disabled={busy} style={{ ...button, padding: '6px 10px', fontSize: 12 }}>Edit</button>
              <button type="button" onClick={() => void removeContact(record, resident)} disabled={busy} style={{ ...button, padding: '6px 10px', fontSize: 12, color: '#b91c1c' }}>Remove</button>
            </div>
          })}
          <div style={{ marginTop: 11 }}><button type="button" onClick={() => setModal({ record })} disabled={busy || record.residents.length >= RESIDENT_CAP} title={record.residents.length >= RESIDENT_CAP ? `This apartment already has ${RESIDENT_CAP} residents.` : ''} style={{ ...button, padding: '7px 11px', fontSize: 12, opacity: record.residents.length >= RESIDENT_CAP ? 0.5 : 1 }}>+ Add resident</button>{record.residents.length >= RESIDENT_CAP && <span style={{ marginLeft: 9, fontSize: 11.5, color: 'var(--color-gray-600)' }}>Apartment full</span>}</div>
        </div>)}
      </div>}
      {visibleRecords.length > shownRecords.length && <div style={{ marginTop: 11, fontSize: 12, color: 'var(--color-gray-600)' }}>Showing the first 100 matches. Narrow the search to manage another apartment.</div>}
    </div>

    {documents.length > 0 && <div style={card}><div style={{ fontSize: 16, fontWeight: 800, marginBottom: 10 }}>Documents awaiting extraction</div>{documents.map(document => <div key={document.id} style={{ fontSize: 13, padding: '8px 0', borderTop: '1px solid var(--color-gray-100)' }}>{document.filename} · queued</div>)}</div>}
    <div style={{ ...card, color: 'var(--color-gray-600)', fontSize: 13 }}>Snap a photo <b>coming soon</b>. Photo capture will not create resident records until document extraction and review are available.</div>

    {modal && <ResidentModal key={`${modal.record.unit}:${modal.resident?.id ?? 'new'}`} unit={modal.record.unit} resident={modal.resident} busy={busy} onClose={() => setModal(null)} onSave={saveContact} />}
  </section>
}
